import { SignInIdentifier } from '@logto/schemas';
import { cond } from '@silverhand/essentials';
import classNames from 'classnames';
import { useCallback, useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { PasswordInputField } from '@/components/InputFields';
import SwitchToVerificationMethodsLink from '@/components/SwitchToVerificationMethodsLink';
import CaptchaBox from '@/containers/CaptchaBox';
import ForgotPasswordLink from '@/containers/ForgotPasswordLink';
import useFocusFirstInvalidField from '@/hooks/use-focus-first-invalid-field';
import usePasswordSignIn from '@/hooks/use-password-sign-in';
import { useForgotPasswordSettings } from '@/hooks/use-sie';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import type { IdentifierInputValue } from '@/shared/components/InputFields/SmartInputField';

import IdentifierSummary from './IdentifierSummary';

type Props = {
  readonly className?: string;
  readonly identifier: SignInIdentifier;
  readonly value: string;
  readonly isVerificationCodeEnabled?: boolean;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
};

type FormState = {
  identifier: IdentifierInputValue;
  password: string;
};

const PasswordForm = ({
  className,
  autoFocus,
  isVerificationCodeEnabled = false,
  identifier,
  value,
}: Props) => {
  const { t } = useTranslation();
  const { errorMessage, clearErrorMessage, onSubmit } = usePasswordSignIn();
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();

  const {
    register,
    handleSubmit,
    control,
    trigger,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    defaultValues: {
      identifier: {
        type: identifier,
        value,
      },
      password: '',
    },
  });

  const formRef = useFocusFirstInvalidField(isSubmitting);

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage();

      await handleSubmit(async ({ identifier: { type, value }, password }) => {
        if (!type) {
          return;
        }

        setIdentifierInputValue({ type, value });

        await onSubmit({
          identifier: { type, value },
          password,
        });
      })(event);
    },
    [clearErrorMessage, handleSubmit, onSubmit, setIdentifierInputValue]
  );

  return (
    <form
      ref={formRef}
      className={classNames('flex flex-col', className)}
      onSubmit={onSubmitHandler}
    >
      <Controller
        control={control}
        name="identifier"
        render={({
          field: {
            name,
            value: { value },
          },
          // `username` is the role this field plays for any identifier type and pairs with
          // `current-password`; the identifier type itself (e.g. `phone`) is not a valid token.
        }) => <input readOnly hidden name={name} value={value} autoComplete="username" />}
      />

      <div className="flex flex-col gap-4">
        <IdentifierSummary identifier={identifier} value={value} />

        <PasswordInputField
          autoFocus={autoFocus}
          autoComplete="current-password"
          label={t('input.password')}
          // A server rejection lands in `errorMessage` but belongs to this field: flagging it
          // sets `aria-invalid` and gives focus-first-invalid a target.
          isDanger={!!errors.password || !!errorMessage}
          errorMessage={errors.password?.message}
          {...register('password', {
            required: t('error.password_required'),
            onChange: () => {
              void trigger('password');
            },
          })}
        />

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        {isForgotPasswordEnabled && (
          <ForgotPasswordLink className="self-start" identifier={identifier} value={value} />
        )}

        <CaptchaBox />
      </div>

      <Button
        className="mt-6"
        title="action.sign_in"
        name="submit"
        htmlType="submit"
        isLoading={isSubmitting}
      />

      <SwitchToVerificationMethodsLink
        hasVerificationCode={identifier !== SignInIdentifier.Username && isVerificationCodeEnabled}
        className="mt-6"
        identifier={cond(identifier !== SignInIdentifier.Username && identifier)}
        value={value}
      />

      <input hidden type="submit" />
    </form>
  );
};

export default PasswordForm;
